import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Flex,
  Textarea,
  useTheme,
  FormErrorMessage,
  Text,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import SubmitButton from "../../buttons/SubmitButton";
import { useToastContext } from "../../toastNotificationProvider/ToastNotificationProvider";
import { getAllAssignment } from "../../../store/slice/assignmentSlice";
import { getAllSubjects } from "../../../store/slice/subjectSlice";
import openFileDialog from "../../../utils/openFileDialog";
import { extractFileName } from "../../../utils/extractFileNameFromUrl";
import { getAllTopics } from "../../../store/slice/topicSlice";
import {
  uploadAssignment,
  updateAssignment,
} from "../../../store/slice/assignmentSlice";

import { useDispatch, useSelector } from "react-redux";

const AddAssignmentModal = ({ isOpen, onClose, editData }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { addNotification } = useToastContext();
  const {
    placeholderOuterColor,
    placeholderTextColor,
    primaryBlueLight,
    primaryBlue,
  } = theme.colors.pallete;
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [formData, setFormData] = useState({
    subjectId: "",
    topicId: "",
    description: "",
    files: "",
  });

  const [errors, setErrors] = useState({});
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);

  const { subjects } = useSelector((state) => state.subject);
  const { topics } = useSelector((state) => state.topics);

  useEffect(() => {
    if (editData) {
      setFormData({
        subjectId: editData.subjectId || "",
        topicId: editData.topicId || "",
        description: editData.description || "",
      });

      setExistingFiles(editData.assignmentFiles || []);
      setNewFiles([]);
    } else {
      setFormData({
        subjectId: "",
        topicId: "",
        description: "",
      });
      setExistingFiles([]);
      setNewFiles([]);
    }
  }, [editData]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        subjectId: "",
        topicId: "",
        description: "",
        files: "",
      });
      setErrors({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllSubjects());
      dispatch(getAllTopics());
    }
  }, [isOpen, dispatch]);

  const handleFileUpload = async () => {
    const files = await openFileDialog();
    if (files.length > 0) {
      setNewFiles((prev) => [...prev, ...files]);
    }
  };

  const handleRemoveExistingFile = (fileToRemove) => {
    console.log("Removing file:", fileToRemove);
    console.log("Before removal:", existingFiles);
    setExistingFiles((prev) => {
      const updated = prev.filter((file) => file.id !== fileToRemove.id);
      console.log("After removal:", updated);
      return updated;
    });
  };

  const handleRemoveNewFile = (index) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.subjectId) newErrors.subjectId = "Subject is required.";
    if (!formData.topicId) newErrors.topicId = "Topic is required.";
    if (!formData.description)
      newErrors.description = "Description is required.";
    if (existingFiles.length === 0 && newFiles.length === 0)
      newErrors.files = "At least one file is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubjectChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      subjectId: Number(selectedOption.value),
      topicId: "",
    }));
    setErrors((prev) => ({ ...prev, subjectId: "" }));
  };

  const handleTopicChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      topicId: Number(selectedOption.value),
    }));
    setErrors((prev) => ({ ...prev, topicId: "" }));
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const formDatas = new FormData();
    formDatas.append("subjectId", formData.subjectId);
    formDatas.append("topicId", formData.topicId);
    formDatas.append("description", formData.description);

    newFiles.forEach((file) => {
      formDatas.append("files", file);
    });

    const removedFileIds = (editData?.assignmentFiles || [])

      .filter(
        (file) =>
          !existingFiles.some((existingFile) => existingFile.id === file.id)
      )
      .map((file) => file.id);

    formDatas.append("deletedFileIds", JSON.stringify(removedFileIds));

    setIsSubmitLoading(true);

    if (editData) {
      handleUpdateAssignment(editData.id, formDatas);
    } else {
      handleCreateAssignment(formDatas);
    }
  };

  const handleUpdateAssignment = (id, formDatas) => {
    dispatch(updateAssignment({ id, formData: formDatas }))
      .unwrap()
      .then((response) => {
        console.log("Assignment updated successfully:", response);
        addNotification("Assignment updated successfully", "success", 3000);
        dispatch(getAllAssignment());
        onClose();
      })
      .catch((error) => {
        console.error("Error updating assignment:", error);
        addNotification("Error updating Assignment", "error", 3000);
      })
      .finally(() => {
        setIsSubmitLoading(false);
      });
  };

  const handleCreateAssignment = (formDatas) => {
    dispatch(uploadAssignment(formDatas))
      .unwrap()
      .then((response) => {
        console.log("Assignment created successfully:", response);
        addNotification("Assignment created successfully", "success", 3000);
        onClose();
      })
      .catch((error) => {
        console.error("Error creating assignment:", error);
        addNotification("Error creating assignment", "error", 4000);
      })
      .finally(() => {
        setIsSubmitLoading(false);
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize={"18px"} fontWeight={400} textAlign="center">
          {editData ? "Update Assignment" : " Add Assignment"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <FormControl isRequired isInvalid={!!errors.subjectId}>
            <FormLabel>Select Subject</FormLabel>
            <Select
              name="subject"
              fontSize="14px"
              borderColor={placeholderOuterColor}
              placeholder="Select Subject"
              value={
                formData.subjectId
                  ? {
                      value: formData.subjectId.toString(),
                      label:
                        subjects.find((s) => s.id === formData.subjectId)
                          ?.name ?? "",
                    }
                  : null
              }
              onChange={handleSubjectChange}
              options={subjects.map((subject) => ({
                value: subject.id.toString(),
                label: subject.name,
              }))}
            />
            <FormErrorMessage>{errors.subjectId}</FormErrorMessage>
          </FormControl>

          <FormControl mt={4} isRequired isInvalid={!!errors.topicId}>
            <FormLabel>Select Topic</FormLabel>
            <Select
              name="topic"
              fontSize="14px"
              borderColor={placeholderOuterColor}
              placeholder="Select Topic"
              value={
                formData.topicId
                  ? {
                      value: formData.topicId,
                      label:
                        topics.find((t) => t.id === formData.topicId)?.name ??
                        "",
                    }
                  : null
              }
              onChange={handleTopicChange}
              options={topics
                .filter((topic) => topic.subjectId === +formData.subjectId)
                .map((topic) => ({
                  value: topic.id.toString(),
                  label: topic.name,
                }))}
              isDisabled={!formData.subjectId}
            />

            <FormErrorMessage>{errors.topicId}</FormErrorMessage>
          </FormControl>

          <FormControl mt={4} isRequired isInvalid={!!errors.description}>
            <FormLabel>Description</FormLabel>
            <Textarea
              name="description"
              borderColor={placeholderOuterColor}
              _placeholder={{ color: placeholderTextColor }}
              fontSize={"14px"}
              placeholder="Enter Description"
              value={formData.description}
              onChange={handleChange}
            />
            <FormErrorMessage>{errors.description}</FormErrorMessage>
          </FormControl>

          <FormControl mt={4} isInvalid={!!errors.files}>
            <Flex my={3}>
              <Flex
                w={"100%"}
                border={"1px solid #E2E8F0"}
                marginRight={2}
                borderRadius={"md"}
                alignItems={"center"}
                pl={4}
                cursor={"pointer"}
                overflowX="auto"
                scrollBehavior="smooth"
              >
                <Text color="#718096">Files to Upload</Text>
              </Flex>{" "}
              <Button
                w={"20%"}
                bg={primaryBlue}
                _hover={{ bg: primaryBlueLight }}
                color="white"
                fontSize={"14px"}
                fontWeight={500}
                py={6}
                px={20}
                onClick={handleFileUpload}
              >
                Select Files
              </Button>
            </Flex>

            <Flex direction="column" gap={2}>
              {existingFiles.map((file, index) => (
                <Flex
                  key={index}
                  justifyContent="space-between"
                  alignItems="center"
                  border="1px solid #E2E8F0"
                  px={3}
                  py={2}
                  borderRadius="md"
                >
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    isTruncated
                    maxWidth="80%"
                    title={extractFileName(file.key)}
                  >
                    {extractFileName(file.key)}
                  </Text>
                  <Button
                    size="xs"
                    colorScheme="red"
                    onClick={() => handleRemoveExistingFile(file)}
                    flexShrink={0}
                  >
                    Remove
                  </Button>
                </Flex>
              ))}
              {newFiles.map((file, index) => (
                <Flex
                  key={index}
                  justifyContent="space-between"
                  alignItems="center"
                  border="1px solid #E2E8F0"
                  px={3}
                  py={2}
                  borderRadius="md"
                >
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    isTruncated
                    maxWidth="80%"
                  >
                    {file.name}
                  </Text>
                  <Button
                    size="xs"
                    colorScheme="red"
                    onClick={() => handleRemoveNewFile(index)}
                    flexShrink={0}
                  >
                    Remove
                  </Button>
                </Flex>
              ))}
            </Flex>
            <FormErrorMessage>{errors.files}</FormErrorMessage>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Flex w={"full"} justifyContent={"center"}>
            <SubmitButton
              text={editData ? "Update" : "Submit"}
              backColor={primaryBlue}
              textColor="white"
              hoverColor={primaryBlueLight}
              isLoading={isSubmitLoading}
              onClickHandler={handleSubmit}
            />
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddAssignmentModal;
